import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Alert } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { useQuizContext } from "@/state/QuizContext";
import { useDogInclusion } from "@/state/useDogInclusion";
import { isDogIncluded, dogPossessive } from "@/helpers/dogInclusion";
import { writeResultsStepToURL } from "@/state/urlState";
import { InclusionSwitch } from "../plan/WhoIsInBoxBlock";
import { useProducts } from "@/state/ProductsContext";
import { SELLING_PLAN_LABELS, SellingPlanKey } from "@/helpers/plansToPacks";
import { PawsDayPicker } from "../deliveryDates/PawsDayPicker";
import { buildHiddenPriceMap } from "../hiddenPlanPricing";
import { fromYmd, computeShipDate, toYmd, isDisabledDate, findNextAllowedDate, lookupShipDate, findFirstAvailableFromApi } from "../deliveryDates/datesHelper";
import { fetchAvailableDatesWithConfig, type QivoraDate } from "@/api/qivoraClient";
import { QUIZ } from "@/theme/quizTokens";
import { useDogPackBlocks } from "../meals/PackMealSummary";
import {
  useExperimentValue,
  QUIZ_PRESENTATION_EXPERIMENT_KEY,
} from "@/experiments/useExperimentValue";
import {
  ReviewHeader,
  MoneyBackBanner,
  FrozenBand,
  WhatYouSayCard,
  OurPromiseBand,
  BowlsLickedBand,
  ProofStrip,
} from "../review/ReviewSections";

const PLAN_KEY_BY_DAYS: Record<number, SellingPlanKey> = {
  7: "DAY_7",
  14: "DAY_14",
  28: "DAY_28",
};

type VariantMeta = {
  title: string;
  image: string;
  grams: number;
  price: number;
};

// One line item in the "See full breakdown" panel — either a meal tub or a
// treat, scoped to the dog it belongs to. Meals don't have a real per-unit
// price in this pricing model (only a per-pack price), so their line price
// is the pack price split evenly across the tubs in that pack, then the
// same intro discount applied to the total is applied per line. Treats use
// their real variant price and are never discounted, matching the totals
// calculation below.
type BreakdownLine = {
  key: string;
  dogName: string;
  title: string;
  qty: number;
  grams?: number;
  priceFull: number; // before intro discount
  priceDiscounted: number; // after intro discount (treats: same as priceFull)
};

const BASE_DISCOUNT = 0.25;
const getDiscountMultiplier = () => 1 - BASE_DISCOUNT;

type PlanDays = 7 | 14 | 28;
type PackSize = 250 | 400 | 600 | 800;
function formatNextBoxDate(deliveryDateYmd: string | null, planDays: number | null) {
  if (!deliveryDateYmd || !planDays) return null;
  const base = fromYmd(deliveryDateYmd);
  if (!base) return null;
  const next = new Date(base);
  next.setDate(next.getDate() + planDays);
  return next.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

export default function ResultsReview() {
  const { state, dispatch } = useQuizContext();

  // Experiment lever (presentation only, default = current copy).
  const savingsLabel = useExperimentValue(
    QUIZ_PRESENTATION_EXPERIMENT_KEY,
    "savings_label",
    "Total Saving"
  );
  const { includedCount, setDogIncluded } = useDogInclusion();
  const {
    planProducts,
    mealProducts,
    treatProducts,
    hiddenPriceProducts,
  } = useProducts();

  const dogs = useMemo(() =>
    state.dogOrder.map((id) => ({
      id,
      form: state.dogForms[id] ?? {},
    })),
    [state.dogOrder, state.dogForms]
  );

  const includedDogs = useMemo(
    () => dogs.filter((d) => isDogIncluded(d.form)),
    [dogs]
  );

  const headerDogName = useMemo(() => {
    const names = includedDogs
      .map((d) => d.form?.name)
      .filter((n): n is string => !!n);

    if (names.length === 0) return "Your dog";
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} & ${names[1]}`;
    return `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]}`;
  }, [includedDogs]);

  const headerPronoun = useMemo(() => {
    if (includedDogs.length === 1) {
      return dogPossessive(includedDogs[0].form?.sex);
    }
    return "their";
  }, [includedDogs]);

  const formatPrice = (pounds: number) => `£${(pounds || 0).toFixed(2)}`;

  const isLoading = !planProducts.length || !mealProducts.length;

  // ✅ Hidden pricing map (planDays -> packSize -> pricePerPack)
  const hiddenPriceMap = useMemo(() => {
    return buildHiddenPriceMap(hiddenPriceProducts ?? []);
  }, [hiddenPriceProducts]);

  // Track pricing errors to show user-facing messages
  const [pricingErrors, setPricingErrors] = useState<string[]>([]);
  const [showBreakdown, setShowBreakdown] = useState(false);

  // Track delivery date errors
  const [dateError, setDateError] = useState<string | null>(null);

  // Qivora dates
  const PAWS_SHOP = "kfuag1-zp.myshopify.com";
  const [qivoraDates, setQivoraDates] = useState<QivoraDate[] | null>(null);
  const [deliveryDayNames, setDeliveryDayNames] = useState<string[]>([]);
  const [cutoffHours, setCutoffHours] = useState<number>(36);
  const [datesLoading, setDatesLoading] = useState(true);

  useEffect(() => {
    fetchAvailableDatesWithConfig(PAWS_SHOP)
      .then((res) => {
        setQivoraDates(res.dates);
        setDeliveryDayNames(res.allowed_delivery_names);
        setCutoffHours(res.cutoff_hours);
      })
      .catch(() => setQivoraDates(null))
      .finally(() => setDatesLoading(false));
  }, []);

  // Validate pricing data and collect errors
  useEffect(() => {
    if (isLoading) return;

    const errors: string[] = [];

    for (const dog of dogs) {
      // Issue #50: an excluded dog isn't in the box — its plan config must
      // never block checkout.
      if (!isDogIncluded(dog.form)) continue;
      const dogName = dog.form?.name || "Your dog";
      const planDays = Number(dog.form?.selectedPlanDays);
      const packSize = Number(dog.form?.selectedPackSize);
      const mealSel = (dog.form?.mealSelection ?? []) as { variantId: string; count: number }[];
      const hasMeals = mealSel.some(item => item?.count > 0);

      if (hasMeals) {
        if (!planDays || ![7, 14, 28].includes(planDays)) {
          errors.push(`${dogName}: Please select a valid plan duration.`);
        } else if (!packSize || ![250, 400, 600, 800].includes(packSize)) {
          errors.push(`${dogName}: Please select a valid pack size.`);
        } else {
          const pricePerPack = Number(hiddenPriceMap?.[planDays as 7 | 14 | 28]?.[packSize as 250 | 400 | 600 | 800] ?? 0);
          if (!pricePerPack || pricePerPack <= 0) {
            errors.push(`${dogName}: Unable to calculate price for ${packSize}g / ${planDays} day plan. Please try a different option.`);
          }
        }
      }
    }

    setPricingErrors(errors);
  }, [dogs, hiddenPriceMap, isLoading]);

  // Build variant lookup (variantId → product data) from meals + treats + Christmas specials
  const variantLookup: Record<string, VariantMeta> = useMemo(() => {
    const out: Record<string, VariantMeta> = {};

    const allProducts = [
      ...(mealProducts ?? []),
      ...(treatProducts ?? []),
    ];

    for (const product of allProducts) {
      for (const variant of product.variants ?? []) {
        const gramsFromTitle = parseInt(String(variant.title), 10);
        const grams =
          variant.grams && Number(variant.grams) > 0
            ? Number(variant.grams)
            : Number.isFinite(gramsFromTitle)
              ? gramsFromTitle
              : 0;

        out[String(variant.id)] = {
          title: String(product.title ?? ""),
          image: Array.isArray(product.image)
            ? (product.image[0] as string)
            : (product.image as string),
          grams,
          price: Number(variant.price),
        };
      }
    }

    return out;
  }, [mealProducts, treatProducts]);

  // Auto-select / validate the delivery date (must run on every render pass — before
  // any early return — to satisfy the rules of hooks).
  useEffect(() => {
    // Wait for Qivora dates to load before auto-selecting
    if (datesLoading) return;

    const raw = state.deliveryDate;
    const now = new Date();

    // Helper: pick first available date (Qivora API or hardcoded fallback)
    const pickNext = (): { ymd: string; shipYmd: string } | null => {
      if (qivoraDates?.length) {
        const first = findFirstAvailableFromApi(qivoraDates);
        if (first) return { ymd: first.date, shipYmd: first.ship_date };
      }
      const next = findNextAllowedDate(now);
      if (next) return { ymd: toYmd(next), shipYmd: toYmd(computeShipDate(next)) };
      return null;
    };

    // Helper: check if a date is valid in the current set
    const isValid = (dateObj: Date): boolean => {
      if (qivoraDates) {
        const ymd = toYmd(dateObj);
        return qivoraDates.some((d) => d.date === ymd);
      }
      return !isDisabledDate(dateObj, now);
    };

    // Case 1: No delivery date set yet - auto-select next available
    if (!raw) {
      const next = pickNext();
      if (!next) {
        setDateError("No delivery dates available. Please contact support.");
        return;
      }
      setDateError(null);
      dispatch({ type: "SET_DELIVERY_DATES", deliveryDate: next.ymd, shipDate: next.shipYmd });
      return;
    }

    // Case 2: Date string is malformed/corrupt
    const selected = fromYmd(raw);
    if (!selected) {
      const next = pickNext();
      if (!next) {
        setDateError("No delivery dates available. Please contact support.");
        return;
      }
      setDateError(null);
      dispatch({ type: "SET_DELIVERY_DATES", deliveryDate: next.ymd, shipDate: next.shipYmd });
      return;
    }

    // Case 3: Selected date is no longer valid
    if (!isValid(selected)) {
      const next = pickNext();
      if (!next) {
        setDateError("No delivery dates available. Please contact support.");
        return;
      }
      setDateError(null);
      dispatch({ type: "SET_DELIVERY_DATES", deliveryDate: next.ymd, shipDate: next.shipYmd });
      return;
    }

    // Case 4: Date is valid but ship date might need updating from Qivora
    if (qivoraDates) {
      const apiShip = lookupShipDate(qivoraDates, raw);
      if (apiShip && apiShip !== state.shipDate) {
        dispatch({ type: "SET_DELIVERY_DATES", deliveryDate: raw, shipDate: apiShip });
      }
    }

    setDateError(null);
  }, [state.deliveryDate, dispatch, datesLoading, qivoraDates]);

  if (isLoading) {
    return (
      <Box sx={{ textAlign: "center", mt: 6 }}>
        <Typography>Loading order review...</Typography>
      </Box>
    );
  }

  // CALCULATE TOTALS
  let grandTotalMealsFull = 0; // meals BEFORE intro discount (still includes plan-length pricing)
  let grandTotalDiscountedMeals = 0; // meals AFTER intro discount
  let grandTotalTreats = 0; // treats (inc. Christmas special)
  let firstDogPlanDays: number | null = null;

  const discountMultiplier = getDiscountMultiplier();

  // Line items for the "See full breakdown" panel — one per meal variant /
  // treat variant actually selected, built alongside the totals below.
  const breakdownLines: BreakdownLine[] = [];

  // Same field-reading logic as the pricing-validation effect above, but
  // accumulating totals instead of collecting error strings. Meal pricing
  // uses the hidden per-pack price (not variant.price); treats use the
  // variant price directly and are NOT discounted.
  for (const dog of includedDogs) {
    const dogName = dog.form?.name || "Your dog";
    const mealSel = (dog.form?.mealSelection ?? []) as { variantId: string; count: number }[];
    const hasMeals = mealSel.some((item) => item?.count > 0);

    const rawPlanDays = Number(dog.form?.selectedPlanDays);
    const rawPackSize = Number(dog.form?.selectedPackSize);
    const planDays = ([7, 14, 28].includes(rawPlanDays) ? rawPlanDays : null) as PlanDays | null;
    const packSize = ([250, 400, 600, 800].includes(rawPackSize) ? rawPackSize : null) as PackSize | null;

    if (hasMeals && planDays && packSize) {
      const pricePerPack = Number(hiddenPriceMap?.[planDays]?.[packSize] ?? 0);
      const totalTubs = mealSel.reduce((sum, item) => sum + (Number(item?.count) || 0), 0);
      // Meals only have a per-pack price, not a per-tub price — split the
      // pack price evenly across tubs so each line has a defensible price.
      const pricePerTub = totalTubs > 0 ? pricePerPack / totalTubs : 0;

      const dogMealsFull = pricePerPack * totalTubs; // BEFORE intro discount
      const dogMealsDiscounted = dogMealsFull * discountMultiplier; // AFTER intro discount

      grandTotalMealsFull += dogMealsFull;
      grandTotalDiscountedMeals += dogMealsDiscounted;

      if (firstDogPlanDays === null) {
        firstDogPlanDays = planDays;
      }

      for (const item of mealSel) {
        const qty = Number(item?.count) || 0;
        if (qty <= 0) continue;
        const meta = variantLookup[String(item.variantId)];
        const lineFull = pricePerTub * qty;

        breakdownLines.push({
          key: `${dog.id}-meal-${item.variantId}`,
          dogName,
          title: meta?.title || "Meal",
          qty,
          grams: meta?.grams,
          priceFull: lineFull,
          priceDiscounted: lineFull * discountMultiplier,
        });
      }
    }

    // Treats live in global state, keyed by dog id: { [variantId]: count }
    const dogTreats = (state.treatSelections && state.treatSelections[dog.id]) || {};
    for (const [variantId, count] of Object.entries(dogTreats)) {
      const qty = Number(count);
      if (!qty) continue;
      const meta = variantLookup[variantId];
      if (meta) {
        grandTotalTreats += meta.price * qty;

        breakdownLines.push({
          key: `${dog.id}-treat-${variantId}`,
          dogName,
          title: meta.title,
          qty,
          priceFull: meta.price * qty,
          priceDiscounted: meta.price * qty, // treats are not discounted
        });
      }
    }
  }

  // Issue #50: jump back to a specific dog's meal step from its review card.
  // The one-shot flag tells the meal step to scroll to this dog on arrival;
  // keying the scroll off activeDogId alone would also fire on every normal
  // Plan → Meals advance, since activeDogId is always backfilled.
  const handleEditMeals = (dogId: string) => {
    try {
      sessionStorage.setItem("paws_scroll_to_dog", dogId);
    } catch { }
    dispatch({ type: "SET_ACTIVE_DOG", dogId });
    dispatch({ type: "SET_RESULTS_STEP", step: 3 });
    writeResultsStepToURL(3);
  };

  const multiDog = dogs.length > 1;
  const expandedReviewLayout = includedCount > 1;

  // Per-dog cards — banner + meal rows + FREE items, plus (when there's
  // more than one dog) an edit-meals link and an include/exclude switch on
  // each box, and a collapsed row for any dog switched out of the box.
  // Treats still count toward TOTAL above even though they have no
  // line-item row in this component.
  const dogBlocks = useDogPackBlocks(dogs, variantLookup, {
    multiDog,
    includedCount,
    onEditMeals: handleEditMeals,
    onToggleInclusion: setDogIncluded,
  });
  const includedBlocks = dogBlocks.filter((block) => block.included);
  const excludedBlocks = dogBlocks.filter((block) => !block.included);

  const TOTAL_MEALS_FULL = grandTotalMealsFull;
  const TOTAL_MEALS = grandTotalDiscountedMeals;
  const TOTAL_TREATS = grandTotalTreats;

  const TOTAL_FULL = TOTAL_MEALS_FULL + TOTAL_TREATS;
  const TOTAL = TOTAL_MEALS + TOTAL_TREATS;

  const TOTAL_SAVINGS = Math.max(0, TOTAL_FULL - TOTAL);
  void TOTAL_MEALS; // used in totals; kept for clarity

  const perDay = firstDogPlanDays ? TOTAL / firstDogPlanDays : null;
  const nextBoxDateLabel = formatNextBoxDate(state.deliveryDate ?? null, firstDogPlanDays);

  // Group breakdown lines by dog for the multi-dog breakdown panel; single-dog
  // orders render one flat list with no dog sub-headers.
  const breakdownGroups: [string | null, BreakdownLine[]][] = multiDog
    ? Object.entries(
        breakdownLines.reduce<Record<string, BreakdownLine[]>>((acc, line) => {
          (acc[line.dogName] ||= []).push(line);
          return acc;
        }, {})
      )
    : [[null, breakdownLines]];

  return (
    <Box sx={{ pb: { xs: 0, md: 0 } }}>
      {/* REVIEW heading + chip */}
      <ReviewHeader dogName={headerDogName} pronoun={headerPronoun} dogCount={includedDogs.length} />

      {/* Pricing error banner (functional state, kept from the previous design) */}
      {pricingErrors.length > 0 && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: "12px",
            maxWidth: 760,
            mx: "auto",
            "& .MuiAlert-message": { width: "100%" },
          }}
        >
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
            There's an issue with your order
          </Typography>
          {pricingErrors.map((error, idx) => (
            <Typography key={idx} sx={{ fontSize: "14px" }}>
              • {error}
            </Typography>
          ))}
        </Alert>
      )}

      {/* Delivery date first, then meal boxes; multi-dog boxes fill two desktop columns. */}
      <Box
        sx={{
          maxWidth: { xs: "100%", md: 1100 },
          mx: "auto",
          display: "flex",
          flexDirection: { xs: "column", md: expandedReviewLayout ? "column" : "row" },
          gap: { xs: "26px", md: "44px" },
          alignItems: { xs: "stretch", md: "flex-start" },
        }}
      >
        {/* Order details / contents */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            order: 2,
            background: QUIZ.color.cream2,
            borderRadius: { xs: 0, md: expandedReviewLayout ? 0 : "16px" },
            mx: { xs: "-23px", md: 0 },
            width: { xs: "calc(100% + 46px)", md: expandedReviewLayout ? "100vw" : "auto" },
            maxWidth: "none",
            position: "relative",
            left: { md: expandedReviewLayout ? "50%" : 0 },
            transform: { md: expandedReviewLayout ? "translateX(-50%)" : "none" },
            boxSizing: "border-box",
            px: { xs: 0, md: expandedReviewLayout ? "32px" : 0 },
            py: { xs: "16px", md: "20px" },
          }}
        >
          <Box sx={{ width: "100%", maxWidth: { md: includedCount === 2 || includedCount === 4 ? 860 : 1100 }, mx: "auto" }}>
            {/* Combined step heading — shown once above all dog boxes, not per-dog.
                Sits above dogBlocks so it reads as the header of the meal card,
                matching the "STEP 2 OF 2 / IN FIFI'S FIRST BOX" mockup. */}
            <Typography
              sx={{
                fontFamily: QUIZ.font.label,
                fontWeight: 700,
                fontSize: { xs: "13px", md: "18px" },
                letterSpacing: "1px",
                textTransform: "uppercase",
                color: QUIZ.color.brown,
                textAlign: "center",
                px: "12px",
                mb: "8px",
              }}
            >
              Step 2 of 2
            </Typography>
            <Typography
              sx={{
                fontFamily: QUIZ.font.display,
                fontWeight: 900,
                textTransform: "uppercase",
                color: QUIZ.color.brown,
                textAlign: "center",
                fontSize: { xs: "30px", md: "35px" },
                lineHeight: 1.1,
                px: "12px",
                mb: "20px",
              }}
            >
              {includedDogs.length > 1 ? "In Your Dogs' First Box" : `In ${headerDogName}'s First Box`}
            </Typography>

            <Box sx={{ display: { md: expandedReviewLayout ? "none" : "block" } }}>
              {dogBlocks.map((block) => block.content)}
            </Box>

            {expandedReviewLayout && (
              <>
                {excludedBlocks.length > 0 && (
                  <Box sx={{ display: { xs: "none", md: "flex" }, justifyContent: "center", flexWrap: "wrap", gap: "20px", mb: "24px" }}>
                    {excludedBlocks.map((block) => (
                      <Box
                        key={block.dogId}
                        sx={{
                          width: excludedBlocks.length === 3 ? "calc((100% - 40px) / 3)" : { md: 320, lg: 340 },
                          maxWidth: "100%",
                        }}
                      >
                        {block.content}
                      </Box>
                    ))}
                  </Box>
                )}
                <Box
                  sx={{
                    display: { xs: "none", md: "grid" },
                    gridTemplateColumns: { md: "repeat(2, minmax(0, 1fr))", lg: "repeat(6, minmax(0, 1fr))" },
                    gap: "24px",
                    alignItems: "stretch",
                  }}
                >
                  {includedBlocks.map((block, index) => {
                    const centerLastOnMedium = includedCount % 2 === 1 && index === includedCount - 1;
                    const largeColumns = includedCount === 3 || includedCount === 5 ? 3 : 2;
                    const largeColumn = includedCount === 5 && index >= 3
                      ? index === 3 ? 2 : 4
                      : largeColumns === 3 ? (index % 3) * 2 + 1 : (index % 2) * 3 + 1;

                    return (
                      <Box
                        key={block.dogId}
                        sx={{
                          gridRow: { md: Math.floor(index / 2) + 1, lg: Math.floor(index / largeColumns) + 1 },
                          gridColumn: {
                            md: centerLastOnMedium ? "1 / -1" : `${index % 2 + 1} / span 1`,
                            lg: `${largeColumn} / span ${largeColumns === 3 ? 2 : 3}`,
                          },
                          width: "100%",
                          maxWidth: { md: centerLastOnMedium ? 380 : "none", lg: "none" },
                          mx: { md: centerLastOnMedium ? "auto" : 0, lg: 0 },
                        }}
                      >
                        {block.content}
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* Delivery date card */}
        <Box sx={{ width: { xs: "100%", md: expandedReviewLayout ? 620 : 520 }, mx: { md: expandedReviewLayout ? "auto" : 0 }, flexShrink: 0, order: 1 }}>
          <Box
            sx={{
              px: { xs: 0, md: "22px" },
              py: { xs: "12px", md: "22px" },
            }}
          >
            {/* Step heading for the delivery-date card — sits above
                PawsDayPicker so it reads as the header of the calendar card,
                matching the "STEP 1 OF 2 / WHEN SHOULD WE DELIVER?" mockup. */}
            <Typography
              sx={{
                fontFamily: QUIZ.font.label,
                fontWeight: 700,
                fontSize: { xs: "13px", md: "18px" },
                letterSpacing: "1px",
                textTransform: "uppercase",
                color: QUIZ.color.brown,
                textAlign: "center",
                px: "12px",
                mb: "8px",
              }}
            >
              Step 1 of 2
            </Typography>
            <Typography
              sx={{
                fontFamily: QUIZ.font.display,
                fontWeight: 900,
                textTransform: "uppercase",
                color: QUIZ.color.brown,
                textAlign: "center",
                fontSize: { xs: "30px", md: "35px" },
                lineHeight: 1.1,
                px: "12px",
                mb: "20px",
              }}
            >
              When Should We Deliver?
            </Typography>

            <PawsDayPicker
              value={state.deliveryDate}
              onChange={({ deliveryDate, shipDate }) => {
                dispatch({ type: "SET_DELIVERY_DATES", deliveryDate, shipDate });
              }}
              qivoraDates={qivoraDates}
              loading={datesLoading}
              deliveryDayNames={deliveryDayNames}
              cutoffHours={cutoffHours}
              suppressFooter={!!dateError}
            />

            {dateError && (
              <Box sx={{ mt: "12px", bgcolor: QUIZ.color.red, borderRadius: "12px", p: "14px 16px" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "6px" }}>
                  <ErrorOutlineIcon sx={{ fontSize: 18, color: QUIZ.color.white }} />
                  <Typography
                    sx={{
                      fontFamily: QUIZ.font.body,
                      fontWeight: 600,
                      fontSize: "11px",
                      letterSpacing: "0.8px",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.9)",
                    }}
                  >
                    Invalid selection
                  </Typography>
                </Box>
                <Typography sx={{ fontFamily: QUIZ.font.body, fontWeight: 700, fontSize: "16px", lineHeight: 1.3, color: QUIZ.color.white }}>
                  Date unavailable – please select a new date
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: { md: "grid" },
          gridTemplateColumns: { md: "repeat(2, minmax(0, 1fr))" },
          gap: { md: "32px" },
          alignItems: "start",
          maxWidth: { md: 1100 },
          mx: "auto",
          mt: { md: "40px" },
          mb: { md: "40px" },
        }}
      >
      {/* Price and money-back reassurance share a row on desktop. */}
      {/* Price — redesigned as a card matching the mockup */}
      <Box sx={{ width: "100%", maxWidth: { xs: 480, md: "100%" }, mx: "auto", mt: { xs: "26px", md: 0 }, minWidth: 0 }}>
        <Box
          sx={{
            background: QUIZ.color.brown,
            borderRadius: "20px",
            p: "20px",
            color: QUIZ.color.white,
          }}
        >
          {/* Plan title */}
          <Typography
            sx={{
              fontFamily: QUIZ.font.label,
              fontWeight: 800,
              fontSize: { xs: "22px", md: "26px" },
              letterSpacing: "1px",
              textTransform: "uppercase",
              color: QUIZ.color.white,
              textAlign: "center",
              mb: "6px",
            }}
          >
            {includedDogs.length > 1 ? "Your Total" : `${headerDogName}'s Total`}
          </Typography>

          {/* White inner card: plan + price, with per-day pill nested inside */}
          <Box
            sx={{
              background: QUIZ.color.white,
              borderRadius: "14px",
              p: "16px 18px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontFamily: QUIZ.font.display,
                  fontWeight: 900,
                  fontSize: { xs: "15px", md: "18px" },
                  textTransform: "uppercase",
                  color: QUIZ.color.brown,
                }}
              >
                {includedDogs.length > 1
                  ? `${includedDogs.length} Dogs · ${firstDogPlanDays ?? "—"} Days`
                  : firstDogPlanDays
                    ? `${firstDogPlanDays} Day Plan`
                    : "Your Plan"}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: QUIZ.font.display,
                    fontWeight: 900,
                    fontSize: { xs: "26px", md: "32px" },
                    color: QUIZ.color.brown,
                  }}
                >
                  {formatPrice(TOTAL)}
                </Typography>
                {TOTAL_FULL > TOTAL && (
                  <Typography
                    sx={{
                      fontFamily: QUIZ.font.body,
                      fontSize: "15px",
                      fontWeight: 900,
                      color: QUIZ.color.red,
                      textDecoration: "line-through",

                    }}
                  >
                    {formatPrice(TOTAL_FULL)}
                  </Typography>
                )}
              </Box>
            </Box>

            {perDay != null && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  pr: { xs: "8px", md: "18px" },
                  mt: "10px",
                  position: "relative",
                  zIndex: 2,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    background: QUIZ.color.cream3,
                    color: QUIZ.color.brown,
                    borderRadius: "20px",
                    px: "14px",
                    py: "4px",
                    fontFamily: QUIZ.font.body,
                    fontWeight: 700,
                    fontSize: "13px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatPrice(perDay)}
                  {TOTAL_FULL > TOTAL && (
                    <Box
                      component="span"
                      sx={{ textDecoration: "line-through", opacity: 0.6, ml: "6px" }}
                    >
                      {formatPrice(TOTAL_FULL / (firstDogPlanDays || 1))}
                    </Box>
                  )}
                  {" per day"}
                </Box>
              </Box>
            )}
          </Box>

          {/* Savings pill */}
          {TOTAL_SAVINGS > 0 && (
            <Box sx={{ mt: "14px", width: "100%" }}>
              <Box
                sx={{
                  width: "100%",
                  boxSizing: "border-box",
                  background: QUIZ.color.green,
                  color: QUIZ.color.brown,
                  borderRadius: "24px",
                  px: "22px",
                  py: "8px",
                  fontFamily: QUIZ.font.label,
                  fontWeight: 800,
                  fontSize: { xs: "14px", md: "16px" },
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  textAlign: "center",
                }}
              >
                You Save {formatPrice(TOTAL_SAVINGS)}
              </Box>
            </Box>
          )}

          {/* Breakdown toggle — controls the per-product line items panel below */}
          <Box
            onClick={() => setShowBreakdown((v) => !v)}
            sx={{
              mt: "14px",
              background: QUIZ.color.brown,
              borderRadius: "24px",
              px: "18px",
              py: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              border: `1px solid ${QUIZ.color.white}`,
            }}
          >
            <Typography
              sx={{
                fontFamily: QUIZ.font.body,
                fontWeight: 700,
                fontSize: "14px",
                color: QUIZ.color.white,

              }}
            >
              {showBreakdown ? "Hide full breakdown" : "See full breakdown"}
            </Typography>
            <Typography sx={{ color: QUIZ.color.white, fontSize: "14px" }}>
              {showBreakdown ? "▲" : "▼"}
            </Typography>
          </Box>

          {/* Per-product breakdown — white card, matching the mockup */}
          {showBreakdown && breakdownLines.length > 0 && (
            <Box
              sx={{
                mt: "12px",
                bgcolor: QUIZ.color.white,
                borderRadius: "14px",
                p: "16px 18px",
              }}
            >
              {breakdownGroups.map(([dogName, lines]) => (
                <Box key={dogName ?? "single"}>
                  {multiDog && dogName && (
                    <Typography
                      sx={{
                        fontFamily: QUIZ.font.body,
                        fontSize: "12px",
                        fontWeight: 800,
                        letterSpacing: "0.4px",
                        textTransform: "uppercase",
                        color: QUIZ.color.grey,
                        mt: "6px",
                        mb: "4px",
                      }}
                    >
                      {dogName}
                    </Typography>
                  )}
                  {lines.map((line) => (
                    <Box
                      key={line.key}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: "10px",
                        gap: 1,
                        borderBottom: `1px solid ${QUIZ.color.cream3}`,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: QUIZ.font.body,
                          fontSize: { xs: "13px", md: "14.5px" },
                          fontWeight: 500,
                          color: QUIZ.color.brown,
                          minWidth: 0,
                        }}
                      >
                        {line.title}
                        {line.grams ? ` (${line.grams}g)` : ""} × {line.qty}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "baseline", gap: "6px", flexShrink: 0 }}>
                        <Typography
                          sx={{
                            fontFamily: QUIZ.font.body,
                            fontSize: { xs: "13px", md: "14.5px" },
                            fontWeight: 700,
                            color: QUIZ.color.brown,
                          }}
                        >
                          {formatPrice(line.priceDiscounted)}
                        </Typography>
                        {line.priceFull > line.priceDiscounted && (
                          <Typography
                            sx={{
                              fontFamily: QUIZ.font.body,
                              fontSize: "11px",
                              color: QUIZ.color.grey,
                              textDecoration: "line-through",
                            }}
                          >
                            {formatPrice(line.priceFull)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ))}

              {/* Total row */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  pt: "14px",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: QUIZ.font.display,
                    fontWeight: 900,
                    fontSize: { xs: "15px", md: "17px" },
                    textTransform: "uppercase",
                    color: QUIZ.color.brown,
                  }}
                >
                  Total Today
                </Typography>
                <Typography
                  sx={{
                    fontFamily: QUIZ.font.display,
                    fontWeight: 900,
                    fontSize: { xs: "18px", md: "20px" },
                    color: QUIZ.color.brown,
                  }}
                >
                  {formatPrice(TOTAL)}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Dates schedule — always visible, no longer gated behind showBreakdown */}
          <Box sx={{ mt: "12px", px: "4px" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", py: "8px" }}>
              <Typography sx={{ fontFamily: QUIZ.font.body, fontSize: { xs: "12.5px", md: "15px" }, fontWeight: 600 }}>
                Today
              </Typography>
              <Typography sx={{ fontFamily: QUIZ.font.body, fontSize: { xs: "12px", md: "14px" }, fontWeight: 700, color: "#c6f24e" }}>
                {formatPrice(TOTAL)}
              </Typography>
            </Box>

            {nextBoxDateLabel && (
              <Box sx={{ display: "flex", justifyContent: "space-between", py: "8px", borderTop: "1px solid rgba(255,255,255,0.15)" }}>
                <Box>
                  <Typography sx={{ fontFamily: QUIZ.font.body, fontSize: { xs: "12.5px", md: "15px" }, fontWeight: 600 }}>
                    {nextBoxDateLabel}
                  </Typography>
                  <Typography sx={{ fontFamily: QUIZ.font.body, fontSize: { xs: "11px", md: "13px" }, color: "rgba(255,255,255,0.6)" }}>
                    Second box, 25% off too
                  </Typography>
                </Box>
                <Typography sx={{ fontFamily: QUIZ.font.body, fontSize: { xs: "12px", md: "14px" }, fontWeight: 700 }}>
                  {formatPrice(TOTAL)}
                </Typography>
              </Box>
            )}

            {firstDogPlanDays && (
              <Box sx={{ display: "flex", justifyContent: "space-between", py: "8px", borderTop: "1px solid rgba(255,255,255,0.15)" }}>
                <Typography sx={{ fontFamily: QUIZ.font.body, fontSize: { xs: "12.5px", md: "15px" }, fontWeight: 600 }}>
                  Then every {firstDogPlanDays} days
                </Typography>
                <Typography sx={{ fontFamily: QUIZ.font.body, fontSize: { xs: "12px", md: "14px" }, fontWeight: 700 }}>
                  {formatPrice(TOTAL_FULL)}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Cancel-anytime note */}
          <Box
            sx={{
              mt: "16px",
              background: "#bfe9f7",
              borderRadius: "14px",
              p: "14px 16px",
            }}
          >
            <Typography
              sx={{
                fontFamily: QUIZ.font.body,
                fontSize: "13px",
                fontWeight: 400,
                color: QUIZ.color.brown,
                lineHeight: 1.4,
              }}
            >
              <Box component="span" sx={{ fontWeight: 700 }}>
                Change, skip or cancel any time.
              </Box>{" "}
              A few clicks in your account, an email, or a phone call. No notice period, no fee, no questions asked.
            </Typography>
          </Box>
        </Box>


      </Box>

      {/* Money-back guarantee section — sits right after the price breakdown card */}
      <Box sx={{ width: "100%", maxWidth: { xs: 480, md: "100%" }, mx: "auto", mt: { xs: "26px", md: 0 }, mb: { xs: "26px", md: 0 }, minWidth: 0 }}>
        <MoneyBackBanner dogName={headerDogName} dogCount={includedDogs.length} />
      </Box>

      </Box>

      {/* Marketing sections (full-bleed) */}
      {/* <FrozenBand />
      <Box sx={{ maxWidth: 1030, mx: "auto" }}>
        <WhatYouSayCard />
      </Box> */}
      <OurPromiseBand />
      <ProofStrip />

      {/* <BowlsLickedBand /> */}

    </Box>
  );
}
